import { Link } from 'react-router-dom';
import { FiArrowLeft, FiShield, FiLock, FiEye, FiUserCheck, FiDatabase, FiAlertCircle } from 'react-icons/fi';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-100 hover:text-white mb-8 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
            Volver al inicio
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <FiShield className="w-12 h-12" />
            <h1 className="text-4xl font-bold">Política de Privacidad</h1>
          </div>
          <p className="text-blue-100 text-lg">
            Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Introducción */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introducción</h2>
            <p className="text-gray-700 leading-relaxed">
              En Teonila HouseKeeping Management, nos tomamos muy en serio la privacidad de nuestros usuarios. Esta Política de Privacidad
              describe cómo recopilamos, usamos, almacenamos y protegemos su información personal cuando utiliza nuestra
              plataforma de gestión de limpieza.
            </p>
          </section>

          {/* Información que recopilamos */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FiDatabase className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">2. Información que Recopilamos</h2>
            </div>

            <div className="space-y-4 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.1 Información de Registro</h3>
                <p className="leading-relaxed">
                  Cuando crea una cuenta, recopilamos información básica como nombre de usuario, dirección de correo electrónico,
                  nombre completo, número de teléfono y nombre del establecimiento.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.2 Información de Uso</h3>
                <p className="leading-relaxed">
                  Recopilamos información sobre cómo utiliza nuestra plataforma, incluyendo registros de actividad,
                  órdenes de limpieza creadas, habitaciones gestionadas y estadísticas de uso.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-900 mb-2">2.3 Información Técnica</h3>
                <p className="leading-relaxed">
                  Recopilamos datos técnicos como dirección IP, tipo de navegador, sistema operativo,
                  páginas visitadas y tiempo de permanencia en la plataforma.
                </p>
              </div>
            </div>
          </section>

          {/* Uso de la información */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FiUserCheck className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">3. Cómo Usamos su Información</h2>
            </div>

            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Proporcionar, mantener y mejorar nuestros servicios</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Procesar transacciones y enviar notificaciones relacionadas</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Comunicarnos con usted sobre actualizaciones, seguridad y soporte</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Detectar y prevenir fraude y actividades maliciosas</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Cumplir con obligaciones legales y regulatorias</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Analizar el uso de la plataforma para mejorar la experiencia del usuario</span>
              </li>
            </ul>
          </section>

          {/* Seguridad */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FiLock className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">4. Seguridad de los Datos</h2>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal:
            </p>

            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Cifrado de datos en tránsito mediante SSL/TLS</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Cifrado de contraseñas utilizando algoritmos seguros</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Autenticación mediante tokens JWT</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Acceso restringido basado en roles y permisos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Copias de seguridad automáticas y regulares</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Monitoreo continuo de seguridad y auditorías</span>
              </li>
            </ul>
          </section>

          {/* Compartir información */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <FiEye className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">5. Compartir Información</h2>
            </div>

            <p className="text-gray-700 leading-relaxed mb-4">
              No vendemos, alquilamos ni compartimos su información personal con terceros para fines de marketing.
              Podemos compartir información limitada en las siguientes circunstancias:
            </p>

            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Con su consentimiento explícito</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Con proveedores de servicios que nos ayudan a operar la plataforma</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>Para cumplir con requisitos legales o proteger nuestros derechos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span>En caso de fusión, adquisición o venta de activos</span>
              </li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cookies y Tecnologías Similares</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Utilizamos cookies y tecnologías similares para mejorar su experiencia, mantener su sesión activa
              y analizar el uso de la plataforma. Puede gestionar las preferencias de cookies en su navegador.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Para más información, consulte nuestro Aviso de Cookies.
            </p>
          </section>

          {/* Derechos del usuario */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Sus Derechos</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              De acuerdo con el RGPD y otras leyes de protección de datos, usted tiene los siguientes derechos:
            </p>

            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span><strong>Derecho de acceso:</strong> Solicitar una copia de sus datos personales</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span><strong>Derecho de rectificación:</strong> Corregir datos inexactos o incompletos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span><strong>Derecho de supresión:</strong> Solicitar la eliminación de sus datos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span><strong>Derecho de portabilidad:</strong> Recibir sus datos en formato estructurado</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span><strong>Derecho de oposición:</strong> Oponerse al procesamiento de sus datos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">•</span>
                <span><strong>Derecho de limitación:</strong> Solicitar la restricción del procesamiento</span>
              </li>
            </ul>

            <p className="text-gray-700 leading-relaxed mt-4">
              Para ejercer estos derechos, contáctenos a través de los canales indicados en la sección de contacto.
            </p>
          </section>

          {/* Retención de datos */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Retención de Datos</h2>
            <p className="text-gray-700 leading-relaxed">
              Conservamos su información personal durante el tiempo necesario para cumplir con los fines descritos
              en esta política, a menos que la ley requiera o permita un período de retención más largo.
              Cuando los datos ya no sean necesarios, los eliminaremos o anonimizaremos de forma segura.
            </p>
          </section>

          {/* Menores */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <FiAlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">9. Protección de Menores</h2>
                <p className="text-gray-700 leading-relaxed">
                  Nuestros servicios no están dirigidos a menores de 18 años. No recopilamos intencionadamente
                  información personal de menores. Si descubrimos que hemos recopilado datos de un menor,
                  los eliminaremos inmediatamente.
                </p>
              </div>
            </div>
          </section>

          {/* Cambios en la política */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Cambios en esta Política</h2>
            <p className="text-gray-700 leading-relaxed">
              Podemos actualizar esta Política de Privacidad ocasionalmente. Le notificaremos cualquier cambio
              significativo publicando la nueva política en esta página y actualizando la fecha de "última actualización".
              Le recomendamos revisar esta política periódicamente.
            </p>
          </section>

          {/* Contacto */}
          <section className="bg-blue-50 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contacto</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Si tiene preguntas, inquietudes o solicitudes relacionadas con esta Política de Privacidad
              o el tratamiento de sus datos personales, puede contactarnos a través de:
            </p>

            <div className="space-y-2 text-gray-700">
              <p><strong>Email:</strong> info@teonila.com</p>
            </div>
          </section>

          {/* Autoridad de control */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Autoridad de Control</h2>
            <p className="text-gray-700 leading-relaxed">
              Si considera que el tratamiento de sus datos personales no es conforme con la normativa de protección de datos,
              tiene derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD) o la autoridad
              de control competente en su país.
            </p>
          </section>
        </div>

        {/* Back button */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
          >
            <FiArrowLeft className="w-5 h-5" />
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
